
import React, { useMemo, useState } from "react";
import type { LegalModuleConfig } from "./types";
import { useLegalCrudModule } from "./useLegalCrudModule";
import { DataToolbar, ActionButtons } from "../../components/DataToolbar";
import { Modal } from "../../components/Modal";
import { PreviewModal } from "../../components/PreviewModal";
import { ContractForm } from "./forms/ContractForm";
import { useAiContext } from "../../ai/AiContext"; // Import Context

// Registry for Forms
const FORM_REGISTRY: Record<string, React.ComponentType<any>> = {
  "ContractForm": ContractForm,
  // Add other forms here later
};

interface Props {
  config: LegalModuleConfig;
}

export function LegalListModule({ config }: Props) {
  const {
    items,
    filteredItems,
    loading,
    error,
    searchText,
    setSearchText,
    showLegacy,
    setShowLegacy,
    handleDelete,
    handleImport,
    handleExport
  } = useLegalCrudModule(config);

  const { setActiveItem } = useAiContext(); // Use Context

  // ✅ 合約留存管理（資料庫）排序：編號年份 / 起始日 / 狀態 / 風險（升冪/降冪）
  type ContractSortKey = "contractYear" | "startDate" | "status" | "risk";
  type SortDir = "asc" | "desc";
  const [sortKey, setSortKey] = useState<ContractSortKey>("startDate");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const getMillis = (v: any): number => {
    if (!v) return 0;
    // Firestore Timestamp
    if (typeof v === "object" && typeof v.toMillis === "function") return v.toMillis();
    // ISO/date-like
    const t = new Date(v).getTime();
    return Number.isNaN(t) ? 0 : t;
  };

  const getStartDateMillis = (row: any): number =>
    getMillis(
      row.periodStart ||
        row.startDate ||
        row.effectiveDate ||
        row.signDate ||
        row.signedDate ||
        row.createdAt
    );

  const getContractYear = (contractNo: any): number => {
    const s = String(contractNo || "");
    // 支援：CT-2026-0003 / LC-2020-0021 / 2026-xxxx
    const m = s.match(/(19\d{2}|20\d{2})/);
    return m ? parseInt(m[1], 10) : 0;
  };

  const statusRank = (raw: any): number => {
    const s = String(raw || "").trim();
    const u = s.toUpperCase();

    // 代碼
    if (u === "NOT_EFFECTIVE" || u === "DRAFT") return 10;
    if (u === "ACTIVE") return 20;
    if (u === "AUTO_RENEWING") return 30;
    if (u === "RENEWAL_NEGOTIATING") return 40;
    if (u === "SUSPENDED") return 50;
    if (u === "EXPIRED") return 60;
    if (u === "TERMINATED") return 70;

    // 中文
    if (s.includes("尚未生效") || s.includes("未生效") || s.includes("草稿")) return 10;
    if (s.includes("生效中") || s.includes("有效")) return 20;
    if (s.includes("自動續約")) return 30;
    if (s.includes("續約洽談")) return 40;
    if (s.includes("暫停履約")) return 50;
    if (s.includes("已屆滿") || s.includes("已到期")) return 60;
    if (s.includes("已解約") || s.includes("已終止")) return 70;

    // 其他：放到中間
    return 35;
  };

  const riskRank = (raw: any): number => {
    const s = String(raw || "").trim().toLowerCase();
    if (s === "high" || s === "高") return 30;
    if (s === "medium" || s === "中") return 20;
    if (s === "low" || s === "低") return 10;
    return 0;
  };

  const contractSortedItems = useMemo(() => {
    if (config.id !== "contracts") return filteredItems;

    const dir = sortDir === "asc" ? 1 : -1;
    const list = [...filteredItems];

    list.sort((a, b) => {
      let va = 0;
      let vb = 0;

      if (sortKey === "contractYear") {
        va = getContractYear(a.contractNo);
        vb = getContractYear(b.contractNo);
        if (va !== vb) return (va - vb) * dir;
        // 同年再用合約編號字串
        const sa = String(a.contractNo || "");
        const sb = String(b.contractNo || "");
        if (sa !== sb) return (sa > sb ? 1 : -1) * dir;
      } else if (sortKey === "startDate") {
        va = getStartDateMillis(a);
        vb = getStartDateMillis(b);
        if (va !== vb) return (va - vb) * dir;
      } else if (sortKey === "status") {
        va = statusRank(a.status);
        vb = statusRank(b.status);
        if (va !== vb) return (va - vb) * dir;
      } else if (sortKey === "risk") {
        va = riskRank(a.riskLevel);
        vb = riskRank(b.riskLevel);
        if (va !== vb) return (va - vb) * dir;
      }

      // tie-breaker：起始日 → 編號 → createdAt
      const ta = getStartDateMillis(a);
      const tb = getStartDateMillis(b);
      if (ta !== tb) return (ta - tb) * -1; // 預設較新的在前

      const ca = String(a.contractNo || a.id || "");
      const cb = String(b.contractNo || b.id || "");
      if (ca !== cb) return ca > cb ? 1 : -1;

      const ra = getMillis(a.createdAt);
      const rb = getMillis(b.createdAt);
      return (rb - ra);
    });

    return list;
  }, [filteredItems, config.id, sortKey, sortDir]);

  // Modal States
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<any | null>(null);
  const [previewItem, setPreviewItem] = useState<any | null>(null);
  
  // Import Modal State
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [isImporting, setIsImporting] = useState(false);

  const FormComponent = FORM_REGISTRY[config.formSchema];

  const onImportSubmit = async () => {
    if (!importFile) return;
    setIsImporting(true);
    await handleImport(importFile);
    setIsImporting(false);
    setIsImportModalOpen(false);
    setImportFile(null);
  };

  const handleSetPreview = (item: any) => {
      setPreviewItem(item);
      // Set AI Context for Generic Lists
      setActiveItem({
          type: (config.id === 'contracts' ? 'contract' : 'other'),
          id: item.contractNo || item.id,
          title: item.title,
          data: item,
          attachments: item.attachments
      });
  };

  const handleSetEdit = (item: any) => {
      setEditingItem(item);
      setIsFormOpen(true);
      // Also set AI context for edit mode to allow helper access
      setActiveItem({
          type: (config.id === 'contracts' ? 'contract' : 'other'),
          id: item.contractNo || item.id,
          title: item.title,
          data: item,
          attachments: item.attachments
      });
  };

  const handleClosePreview = () => {
      setPreviewItem(null);
      setActiveItem(null);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 500 }}>
      {/* Search & Toolbar Area */}
      <div className="section" style={{ marginBottom: 16, padding: "12px 16px", flexShrink: 0 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12 }}>
          <input 
            className="input" 
            style={{ width: 300 }} 
            placeholder={`搜尋${config.searchableFields.join(", ")}...`} 
            value={searchText} 
            onChange={e => setSearchText(e.target.value)} 
          />
          {config.id === "contracts" && (
            <>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 13,
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                <input
                  type="checkbox"
                  checked={showLegacy}
                  onChange={(e) => setShowLegacy(e.target.checked)}
                />
                顯示舊合約
              </label>

              {/* ✅ 排序：編號年份 / 起始日 / 狀態 / 風險（升冪/降冪），預設起始日降冪 */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: 8 }}>
                <span style={{ fontSize: 13, color: "var(--muted)" }}>排序</span>
                <select
                  className="input"
                  // 不要強制 height；避免跟 .input 的 padding 衝突造成文字被裁切
                  style={{ width: 140 }}
                  value={sortKey}
                  onChange={(e) => setSortKey(e.target.value as any)}
                  title="排序欄位"
                >
                  <option value="contractYear">編號年份</option>
                  <option value="startDate">起始日</option>
                  <option value="status">狀態</option>
                  <option value="risk">風險</option>
                </select>

                <select
                  className="input"
                  // 不要強制 height；避免跟 .input 的 padding 衝突造成文字被裁切
                  style={{ width: 110 }}
                  value={sortDir}
                  onChange={(e) => setSortDir(e.target.value as any)}
                  title="排序方向"
                >
                  <option value="desc">降冪</option>
                  <option value="asc">升冪</option>
                </select>
              </div>
            </>
          )}
        </div>
        
        <DataToolbar 
          title={config.title}
          canEdit={!!config.canEdit}
          onCreate={() => { setEditingItem(null); setIsFormOpen(true); setActiveItem(null); }}
          onImport={config.importMapping ? () => setIsImportModalOpen(true) : undefined}
          onExport={handleExport}
        />
      </div>

      {error && <div className="err" style={{marginBottom:16}}>讀取失敗: {error.message}</div>}

      {/* Table Area - Removed 'card' class to fix width constriction, added explicit width 100% */}
      <div style={{ 
          padding: 0, 
          flex: 1, 
          overflow: "hidden", 
          display: "flex", 
          flexDirection: "column", 
          background: "#161616", 
          border: "1px solid var(--border)",
          borderRadius: 16, // Match standard radius
          width: "100%",
          maxWidth: "100%"
      }}>
        <div style={{ overflow: "auto", flex: 1 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead style={{ background: "#222", color: "#aaa", textAlign: "left", position: "sticky", top: 0, zIndex: 1 }}>
              <tr>
                {config.columns.map(col => (
                  <th key={col.key} style={{ padding: "12px 16px", width: col.width, whiteSpace: "nowrap" }}>{col.label}</th>
                ))}
                <th style={{ padding: "12px 16px", textAlign: "right", whiteSpace: "nowrap" }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={config.columns.length + 1} style={{padding:20, textAlign:'center'}}>載入中...</td></tr>}
              {!loading && contractSortedItems.map((row) => (
                <tr key={row.id} style={{ borderBottom: "1px solid #333" }}>
                  {config.columns.map(col => (
                    <td key={col.key} style={{ padding: "12px 16px", verticalAlign: "top" }}>
                      {col.key === 'contractNo' && row.isLegacy && (
                          <span className="badge" style={{marginRight:6, fontSize:10, padding:'2px 4px', background:'#666', color:'#fff'}}>舊</span>
                      )}
                      {col.render ? col.render(row) : (
                        col.type === 'badge' ? <span className="badge">{row[col.key]}</span> :
                        col.type === 'date' ? <span style={{color:'var(--muted)'}}>{row[col.key]}</span> :
                        row[col.key]
                      )}
                    </td>
                  ))}
                  <td style={{ padding: "12px 16px", textAlign: "right" }}>
                    <ActionButtons 
                      canEdit={!!config.canEdit}
                      onPreview={() => handleSetPreview(row)}
                      onEdit={() => handleSetEdit(row)}
                      onDelete={() => handleDelete(row.id, row)}
                    />
                  </td>
                </tr>
              ))}
              {!loading && contractSortedItems.length === 0 && <tr><td colSpan={config.columns.length + 1} style={{padding:40, textAlign:'center', color: 'var(--muted)'}}>無資料</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Form Modal */}
      {isFormOpen && FormComponent && (
        <FormComponent 
          initialData={editingItem}
          allRows={items} 
          onClose={() => { setIsFormOpen(false); setActiveItem(null); }}
          onSuccess={() => { setIsFormOpen(false); setEditingItem(null); setActiveItem(null); }}
        />
      )}

      {/* Preview Modal */}
      {previewItem && <PreviewModal data={previewItem} onClose={handleClosePreview} />}

      {/* Import Modal */}
      {isImportModalOpen && (
        <Modal title="匯入資料" onClose={() => setIsImportModalOpen(false)} width={500}>
           <div className="p" style={{marginBottom:10}}>請選擇 CSV 檔案進行匯入</div>
           <div style={{border:'2px dashed #444', padding:20, textAlign:'center'}}>
              <input type="file" accept=".csv" onChange={e => setImportFile(e.target.files?.[0] || null)} />
           </div>
           <div className="row" style={{marginTop:20, justifyContent:'flex-end'}}>
              <button className="btn" onClick={onImportSubmit} disabled={!importFile || isImporting}>
                 {isImporting ? "匯入中..." : "開始匯入"}
              </button>
           </div>
        </Modal>
      )}
    </div>
  );
}
